import type { ElementType, Ref } from 'react'

import { useRef } from 'react'
import { useGSAP } from '@gsap/react'

import { gsap } from '@/lib/gsap'
import { cn } from '@/lib/utils'

interface AnimatedHeadingProps {
    text: string
    className?: string
    as?: ElementType
    delay?: number
    stagger?: number
    onScroll?: boolean
}

export const AnimatedHeading = ({
    text,
    className,
    as,
    delay = 0.1,
    stagger = 0.075,
    onScroll = false
}: AnimatedHeadingProps) => {
    const Tag = (as ?? 'h2') as ElementType
    const ref = useRef<HTMLElement>(null)
    const words = text.split(' ')

    useGSAP(
        () => {
            const el = ref.current
            if (!el) return

            const inners = el.querySelectorAll('[data-word]')
            const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches

            if (reduce) {
                gsap.set(inners, { yPercent: 0 })
                return
            }

            gsap.set(inners, { yPercent: 118 })

            let played = false
            const play = () => {
                if (played) return
                played = true
                gsap.to(inners, {
                    yPercent: 0,
                    duration: 1,
                    ease: 'power4.out',
                    stagger,
                    delay
                })
            }

            if (!onScroll || typeof IntersectionObserver === 'undefined') {
                play()
                return
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            play()
                            observer.disconnect()
                        }
                    })
                },
                { threshold: 0, rootMargin: '0px 0px -10% 0px' }
            )

            observer.observe(el)

            return () => observer.disconnect()
        },
        { scope: ref, dependencies: [] }
    )

    return (
        <Tag ref={ref as Ref<HTMLElement>} className={cn(className)} aria-label={text}>
            {words.map((word, i) => (
                <span
                    key={`${word}-${i}`}
                    aria-hidden='true'
                    className='inline-block overflow-hidden align-top'
                    style={{ marginRight: '0.24em', paddingBottom: '0.08em' }}
                >
                    <span data-word className='inline-block'>
                        {word}
                    </span>
                </span>
            ))}
        </Tag>
    )
}
